function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && Boolean(value.trim());
}

function hasOwn(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function invalidRelayResponse() {
  const error = new Error("invalid_contact_sign_in_response");
  error.code = "invalid_contact_sign_in_response";
  return error;
}

function invalidPasswordChangeLinkResponse() {
  const error = new Error("invalid_password_change_link_response");
  error.code = "invalid_password_change_link_response";
  return error;
}

function hasOnlyKeys(record, allowed) {
  const keys = Object.keys(record);
  return keys.length === allowed.size && keys.every((key) => allowed.has(key));
}

const SESSION_MATERIAL_FIELDS = [
  "accessToken",
  "refreshToken",
  "refreshExpiresAt",
  "sessionId",
  "accessExpiresIn",
  "contactVerificationDeviceCleared",
  "phoneVerificationDeviceCleared",
  "completionSecret",
  "user",
];

const STATUS_FORBIDDEN_FIELDS = [
  ...SESSION_MATERIAL_FIELDS,
  "signInApproved",
  "returnToSignIn",
  "verified",
  "purpose",
  "error",
  "code",
  "whatsappVerificationMode",
  "whatsappFallbackAvailable",
  "whatsappFallbackEngaged",
  "whatsappFallbackAutomatic",
  "voiceFallbackAvailable",
  "voiceFallbackEngaged",
];

/**
 * Validates the approval-pending response returned only to the browser that
 * owns the completion secret. A normal authenticated response returns null.
 */
export function readPendingContactSignInStatus(value) {
  if (!isRecord(value)) return null;
  const hasStatusSignal =
    value.pendingSignIn === true ||
    value.verificationRequired === true ||
    hasOwn(value, "completionMode") ||
    hasOwn(value, "approvalState") ||
    hasOwn(value, "pollAfterMs");
  if (!hasStatusSignal) return null;

  const valid =
    !STATUS_FORBIDDEN_FIELDS.some((key) => hasOwn(value, key)) &&
    value.ok === true &&
    value.pendingSignIn === true &&
    value.verificationRequired === true &&
    isNonEmptyString(value.challengeId) &&
    value.identityType === "phone" &&
    isNonEmptyString(value.maskedTarget) &&
    value.deliveryChannel === "whatsapp" &&
    value.primaryDeliveryChannel === "whatsapp" &&
    value.fallbackDeliveryChannel === null &&
    value.completionMode === "link_approval" &&
    value.approvalState === "pending" &&
    Number.isFinite(value.pollAfterMs) &&
    value.pollAfterMs > 0 &&
    Number.isFinite(value.expiresInSec) &&
    value.expiresInSec > 0;

  if (!valid) throw invalidRelayResponse();
  return value;
}

/**
 * Validates the deliberately tokenless response shown on the device that
 * opened an India WhatsApp sign-in link. Only the originating browser can
 * exchange its private completion secret for a session.
 */
export function readContactSignInLinkApproval(value) {
  if (!isRecord(value)) return null;
  const hasApprovalSignal =
    value.purpose === "contact_sign_in" ||
    hasOwn(value, "signInApproved") ||
    hasOwn(value, "returnToSignIn") ||
    value.approvalState === "approved";
  if (!hasApprovalSignal) return null;

  const forbiddenFields = [
    ...SESSION_MATERIAL_FIELDS,
    "pendingSignIn",
    "verificationRequired",
    "challengeId",
    "completionMode",
    "pollAfterMs",
    "error",
    "code",
  ];
  const valid =
    !forbiddenFields.some((key) => hasOwn(value, key)) &&
    value.ok === true &&
    value.verified === true &&
    value.purpose === "contact_sign_in" &&
    value.signInApproved === true &&
    value.approvalState === "approved" &&
    value.returnToSignIn === true;

  if (!valid) throw invalidRelayResponse();
  return value;
}

const PASSWORD_CHANGE_LINK_APPROVAL_KEYS = new Set([
  "ok",
  "verified",
  "purpose",
  "approvalState",
  "returnToInitiator",
]);

/**
 * Validates the tokenless approval returned to a password-change link opener.
 * The opener never owns the challenge secret or new password, and therefore
 * must not receive or apply any account/session material.
 */
export function readPasswordChangeLinkApproval(value) {
  if (!isRecord(value) || value.purpose !== "password_change") return null;
  const valid =
    hasOnlyKeys(value, PASSWORD_CHANGE_LINK_APPROVAL_KEYS) &&
    value.ok === true &&
    value.verified === true &&
    value.approvalState === "approved" &&
    value.returnToInitiator === true;
  if (!valid) throw invalidPasswordChangeLinkResponse();
  return value;
}
